import React, { useMemo, useState } from "react";

const API_URL = window.location.origin;

const customFields = [
  { label: "Email", value: "email" },
  { label: "Name", value: "name" },
  { label: "Company", value: "company" },
  { label: "First Name", value: "first_name" },
  { label: "Last Name", value: "last_name" }
];

// Consumer mailbox providers. These can only be sent through their own SMTP
// relay (Gmail rewrites From to the authenticated mailbox), so they are kept
// separate from addresses on domains we own.
const CONSUMER_RE = /^(gmail|googlemail|yahoo|ymail|rocketmail|hotmail|outlook|live|msn|aol|icloud|me|mac|proton|protonmail|gmx|mail|yandex|zoho|fastmail)\.(com|co\.uk|co\.in|de|fr|es|it|ru|net|jp)$/i;

const SEND_MODES = [
  { value: "auto", label: "Auto (Mixed) — domain + gmail senders" },
  { value: "domain", label: "Force Domain — send via your VPS" },
  { value: "gmail", label: "Force Gmail — send via Gmail SMTP relay" }
];

function senderEmailOf(sender) {
  if (!sender) return "";
  if (typeof sender === "string") return sender;
  return String(sender.email || sender.senderEmail || sender.fromEmail || "");
}

function senderKind(sender) {
  const domain = senderEmailOf(sender).split("@")[1] || "";
  return CONSUMER_RE.test(domain) ? "gmail" : "domain";
}

function senderAllowedInMode(sender, mode) {
  if (mode === "gmail") return senderKind(sender) === "gmail";
  if (mode === "domain") return senderKind(sender) === "domain";
  return true;
}

export default function SenderStep({
  form,
  updateForm,
  senders,
  setSenders,
  reloadSenders
}) {
  const [fieldOpen, setFieldOpen] = useState(false);
  const [senderOpen, setSenderOpen] = useState(false);
  const [addSenderOpen, setAddSenderOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [senderForm, setSenderForm] = useState({ name: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [senderError, setSenderError] = useState("");

  const sendMode = form.sendMode || "auto";

  const modeSenders = useMemo(
    () => (senders || []).filter((s) => senderAllowedInMode(s, sendMode)),
    [senders, sendMode]
  );

  const filteredSenders = useMemo(() => {
    const q = search.toLowerCase();
    return modeSenders.filter(
      (s) =>
        (s.name || "").toLowerCase().includes(q) ||
        (s.email || "").toLowerCase().includes(q)
    );
  }, [modeSenders, search]);

  const hiddenCount = (senders || []).length - modeSenders.length;

  function insertCustomField(value) {
    updateForm("subject", `${form.subject || ""} {{${value}}}`.trim());
    setFieldOpen(false);
  }

  async function addSender() {
    if (!senderForm.name.trim() || !senderForm.email.trim()) return;

    try {
      setSaving(true);
      setSenderError("");

      const response = await fetch(`${API_URL}/api/senders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(senderForm)
      });

      const saved = await response.json();

      if (!response.ok) {
        setSenderError(saved.message || "Sender could not be added.");
        return;
      }

      setSenders((prev) => [saved, ...prev]);
      updateForm("fromName", saved.name);
      updateForm("fromEmail", saved.email);
      updateForm("selectedSenders", [saved]);

      setSenderForm({ name: "", email: "" });
      setAddSenderOpen(false);
      setSenderOpen(false);

      if (reloadSenders) reloadSenders();
    } catch (error) {
      setSenderError(error.message);
    } finally {
      setSaving(false);
    }
  }

  const selectedSenders = Array.isArray(form.selectedSenders) ? form.selectedSenders : [];

  const selectedSender = selectedSenders.length
    ? selectedSenders.length === 1
      ? `${selectedSenders[0].name || selectedSenders[0].senderName || "Sender"} <${senderEmailOf(selectedSenders[0])}>`
      : `${selectedSenders.length} senders selected`
    : form.fromEmail
    ? `${form.fromName || "Sender"} <${form.fromEmail}>`
    : "Select sender";

  function applyPrimary(list) {
    if (list[0]) {
      updateForm("fromName", list[0].name || list[0].senderName || "Sender");
      updateForm("fromEmail", senderEmailOf(list[0]));
    } else {
      updateForm("fromName", "");
      updateForm("fromEmail", "");
    }
  }

  // Changing the mode drops any already-selected sender that the new mode
  // cannot deliver, so a Gmail-only campaign never carries a domain sender
  // (and vice versa) into the send queue.
  function changeMode(mode) {
    updateForm("sendMode", mode);
    const kept = selectedSenders.filter((s) => senderAllowedInMode(s, mode));
    if (kept.length !== selectedSenders.length) {
      updateForm("selectedSenders", kept);
      applyPrimary(kept);
    }
    setSearch("");
    setSenderOpen(false);
  }

  function isSenderSelected(sender) {
    const email = senderEmailOf(sender);
    return selectedSenders.some((item) => senderEmailOf(item) === email);
  }

  function toggleSender(sender) {
    const email = senderEmailOf(sender);
    const exists = isSenderSelected(sender);
    const next = exists
      ? selectedSenders.filter((item) => senderEmailOf(item) !== email)
      : [...selectedSenders, sender];

    updateForm("selectedSenders", next);
    applyPrimary(next);
  }

  return (
    <div className="sx-sender-card">
      <div className="sx-field">
        <label>Campaign name <b>*</b></label>
        <p>Used to identify this campaign in your dashboard.</p>
        <input
          value={form.name}
          onChange={(e) => updateForm("name", e.target.value)}
          placeholder="Summer sale announcement"
        />
      </div>

      <div className="sx-field">
        <label>Subject <b>*</b></label>
        <p>This text will be displayed in the Subject field in your recipient's email client.</p>

        <div className="sx-subject-row">
          <input
            value={form.subject}
            onChange={(e) => updateForm("subject", e.target.value)}
            placeholder="Your email subject line"
          />

          <button type="button" className="sx-icon-btn">✧</button>

          <div className="sx-custom-field">
            <button
              type="button"
              className="sx-custom-trigger"
              onClick={() => setFieldOpen(!fieldOpen)}
            >
              Custom Fields <span>{fieldOpen ? "⌃" : "⌄"}</span>
            </button>

            {fieldOpen && (
              <div className="sx-custom-menu">
                {customFields.map((field) => (
                  <button
                    type="button"
                    key={field.value}
                    onClick={() => insertCustomField(field.value)}
                  >
                    <span>{field.label}</span>
                    <em>Default</em>
                  </button>
                ))}
              </div>
            )}
          </div>

          <span className="sx-info">ⓘ</span>
        </div>

        <button
          type="button"
          className="sx-preview-link"
          onClick={() =>
            updateForm(
              "previewText",
              form.previewText ? "" : "Short preview text for your email"
            )
          }
        >
          {form.previewText ? "- Remove preview text" : "+ Add preview text"}
        </button>

        {form.previewText && (
          <input
            className="sx-preview-input"
            value={form.previewText}
            onChange={(e) => updateForm("previewText", e.target.value)}
            placeholder="Preview text"
          />
        )}
      </div>

      <div className="sx-field sx-mode-field">
        <label htmlFor="campaign-send-mode">Send mode <b>*</b></label>
        <p>Chooses how this campaign is delivered. The sender list below only shows addresses this mode can send from.</p>
        <select
          id="campaign-send-mode"
          value={sendMode}
          onChange={(e) => changeMode(e.target.value)}
        >
          {SEND_MODES.map((mode) => (
            <option key={mode.value} value={mode.value}>{mode.label}</option>
          ))}
        </select>
      </div>

      <div className="sx-field">
        <label>Sender <b>*</b></label>
        <p>This will be displayed in the From field of your recipient's email client.</p>

        <div className="sx-sender-select-wrap">
          <button
            type="button"
            className="sx-sender-select"
            onClick={() => setSenderOpen(!senderOpen)}
          >
            <span>{selectedSender}</span>
            <span>⌄</span>
          </button>

          {senderOpen && (
            <div className="sx-sender-menu">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
              />

              {filteredSenders.length === 0 && (
                <p className="sx-empty-small">
                  {sendMode === "gmail"
                    ? "No Gmail sender found. Switch to Auto, or add a Gmail sender."
                    : sendMode === "domain"
                    ? "No domain sender found. Switch to Auto, or add a sender on your domain."
                    : "No sender found."}
                </p>
              )}

              {filteredSenders.map((sender) => (
                <button
                  type="button"
                  key={sender._id || sender.email}
                  onClick={() => toggleSender(sender)}
                >
                  <span>{sender.name || "Sender"} &lt;{sender.email}&gt;</span>
                  {isSenderSelected(sender) && <b>✓</b>}
                </button>
              ))}

              <button
                type="button"
                className="sx-add-sender"
                onClick={() => {
                  setAddSenderOpen(true);
                  setSenderOpen(false);
                }}
              >
                + Add New Sender
              </button>
            </div>
          )}
        </div>

        <p className="sx-selected-senders-help">
          Select one or more senders. Emails will be automatically divided between selected senders.
          {hiddenCount > 0 ? ` ${hiddenCount} sender(s) hidden by the current send mode.` : ""}
        </p>

        <div className="sx-campaign-replyto">
          <label htmlFor="campaign-reply-to">Reply-To email <span>(optional)</span></label>
          <p>Leave blank to use the account default. Replies to this campaign will go to this inbox.</p>
          <input id="campaign-reply-to" type="email" value={form.replyToEmail || ""} onChange={(e) => updateForm("replyToEmail", e.target.value)} placeholder="reply@yourdomain.com" />
        </div>

        <button
          type="button"
          className="sx-add-sender-main"
          onClick={() => setAddSenderOpen(true)}
        >
          + Add New Sender
        </button>
      </div>

      {addSenderOpen && (
        <div className="sx-modal-overlay">
          <div className="sx-small-modal">
            <h2>Add New Sender</h2>
            <p>This sender will be available in future newsletters.</p>

            <label>Sender name</label>
            <input
              value={senderForm.name}
              onChange={(e) =>
                setSenderForm({ ...senderForm, name: e.target.value })
              }
              placeholder="SendX Clone"
            />

            <label>Sender email</label>
            <input
              value={senderForm.email}
              onChange={(e) =>
                setSenderForm({ ...senderForm, email: e.target.value })
              }
              placeholder="sender@example.com"
            />

            {senderError ? <div className="sx-de-msg">{senderError}</div> : null}

            <div className="sx-modal-actions">
              <button type="button" onClick={() => setAddSenderOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="sendx-yellow-btn"
                onClick={addSender}
                disabled={saving}
              >
                {saving ? "Adding..." : "Add Sender"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
